const POLICE_CALL_TYPE_LABELS = {
  "911 HANG-UP": "911 Hang-Up Call",
  "ABANDONED MV": "Abandoned Motor Vehicle",
  "ADMIN SVS": "Administrative Services",
  "AGENCY ASSIST": "Agency Assistance",
  "ASSIST FIRE": "Fire Department Assistance",
  "ASSIST POLICE": "Police Assistance",
  "ATTEMPT-LOCATE": "Attempt to Locate",
  "BACKGROUND INV": "Background Investigation",
  "BOMB THREAT/ATT": "Bomb Threat or Attempt",
  "BRUSH/WOODS FIRE": "Brush or Woods Fire",
  "CO ACCIDENT/NO SYMPTOMS": "Carbon Monoxide Incident – No Symptoms",
  "CO ACCIDENT/WITH SYMPTOMS": "Carbon Monoxide Incident – With Symptoms",
  "CRIM MISCHIEF": "Criminal Mischief",
  "CREST CALLOUT": "Capital Region Emergency Services Team Callout",
  "CREST DIVE CALL OUT": "Capital Region Emergency Services Team Dive Callout",
  "DUI": "Driving Under the Influence",
  "DUMPSTER FIRE/NO EXPOSURES": "Dumpster Fire – No Exposures",
  "DUMPSTER FIRE/WITH STR EXPOSURES": "Dumpster Fire – With Structure Exposures",
  "EMS COMM SERV": "Emergency Medical Services Community Service",
  "EMS PUBLIC PRES": "Emergency Medical Services Public Presentation",
  "EXTRICATION-EQUIP/MACHINE": "Extrication from Equipment or Machinery",
  "FAILURE-APPEAR": "Failure to Appear",
  "FIRE COMM SRV": "Fire Department Community Service",
  "HAZMAT SPILL": "Hazardous Materials Spill",
  "INDUSTRIAL ACC": "Industrial Accident",
  "INJURY-EMPLOYEE": "Employee Injury",
  "JUVENILE PROB": "Juvenile Problem",
  "MVA A/O NO INJ": "Motor Vehicle Accident – No Injuries",
  "MVA DUI": "Motor Vehicle Accident – Driving Under the Influence",
  "MVA EVADING": "Motor Vehicle Accident – Evading Responsibility",
  "MVA FATAL": "Fatal Motor Vehicle Accident",
  "MVA FLUID SPILL": "Motor Vehicle Accident – Fluid Spill",
  "MVA LATE REPORT": "Motor Vehicle Accident – Late Report",
  "MVA PRIVATE PROPERTY": "Motor Vehicle Accident – Private Property",
  "MVA PROP DMG": "Motor Vehicle Accident – Property Damage",
  "MVA W/INJURY": "Motor Vehicle Accident – With Injury",
  "MTS CALLOUT": "Metro Traffic Services Callout",
  "MUTUAL AID EMS": "Mutual Aid – Emergency Medical Services",
  "OVERNIGHT PARK": "Overnight Parking",
  "POLICE INFO": "Police Information",
  "PUBLIC PRESENT": "Public Presentation",
  "RECOVERED VEH": "Recovered Vehicle",
  "RUNAWAY JUVNILE": "Runaway Juvenile",
  "SITE LINE PROBLEM": "Sight Line Problem",
  "SMOKE/ODOR INSIDE": "Smoke or Odor Inside",
  "SMOKE/ODOR OUTDOORS": "Smoke or Odor Outdoors",
  "TRANSFORMER FIRE/ARCING": "Transformer Fire or Arcing",
  "UNSECURE PREMISE": "Unsecured Premises",
  "VIN INSPECTION": "Vehicle Identification Number Inspection",
  "VTO": "Violation of Town Ordinance",
  "WATER EMERGENCY/BOATING": "Water or Boating Emergency",
  "YOUTH PRES": "Youth Presentation",
};

export function titleCaseCallType(value) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  const expanded = POLICE_CALL_TYPE_LABELS[normalized.toUpperCase()];
  if (expanded) return expanded;
  return normalized
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function formatMonth(month) {
  const [year, number] = month.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(number) - 1]} ${year}`;
}

export function buildCrimeSeries(summary) {
  const months = Object.keys(summary?.months || {}).sort();
  const callTypes = summary?.callTypes || [];
  return {
    months,
    labels: months.map(formatMonth),
    series: callTypes.map((callType, index) => ({
      key: callType,
      label: titleCaseCallType(callType),
      valueType: "count",
      data: months.map((month) => (
        summary.firstObservedMonths?.[index] && month < summary.firstObservedMonths[index]
          ? null
          : summary.months[month][index] || 0
      )),
    })),
  };
}

export function firstObservedMonth(summary, callType) {
  const typeIndex = summary?.callTypes?.indexOf(callType) ?? -1;
  if (typeIndex < 0) return null;
  return summary.firstObservedMonths?.[typeIndex] || null;
}

export function availableYears(summary) {
  return [...new Set(Object.keys(summary?.months || {}).map((month) => Number(month.slice(0, 4))))].sort((a, b) => a - b);
}

export function completeMonthCount(summary, year) {
  const through = summary?.throughDateExclusive;
  if (!through) return 12;
  const throughYear = Number(through.slice(0, 4));
  if (year < throughYear) return 12;
  if (year > throughYear) return 0;
  return Math.max(0, Number(through.slice(5, 7)) - 1);
}

export function totalForPeriod(summary, callType, year, monthCount = 12, startMonth = 1) {
  const typeIndex = summary?.callTypes?.indexOf(callType) ?? -1;
  if (typeIndex < 0) return 0;
  let total = 0;
  for (let month = startMonth; month <= monthCount; month += 1) {
    const key = `${year}-${String(month).padStart(2, "0")}`;
    total += summary.months?.[key]?.[typeIndex] || 0;
  }
  return total;
}

export function compareYears(summary, callType, firstYear, secondYear) {
  const endMonth = Math.min(
    completeMonthCount(summary, firstYear),
    completeMonthCount(summary, secondYear),
  );
  const observed = firstObservedMonth(summary, callType);
  const observedYear = observed ? Number(observed.slice(0, 4)) : null;
  const observedMonth = observed ? Number(observed.slice(5, 7)) : 1;
  const firstTracked = observedYear != null && firstYear >= observedYear;
  const secondTracked = observedYear != null && secondYear >= observedYear;
  const startMonth = Math.max(
    firstYear === observedYear ? observedMonth : 1,
    secondYear === observedYear ? observedMonth : 1,
  );
  const first = firstTracked && endMonth >= startMonth
    ? totalForPeriod(summary, callType, firstYear, endMonth, startMonth)
    : null;
  const second = secondTracked && endMonth >= startMonth
    ? totalForPeriod(summary, callType, secondYear, endMonth, startMonth)
    : null;
  const difference = first == null || second == null ? null : second - first;
  return {
    months: Math.max(0, endMonth - startMonth + 1),
    startMonth,
    endMonth,
    first,
    second,
    difference,
    percentChange: first ? (difference / first) * 100 : null,
  };
}

export function monthRangeLabel(monthCount) {
  const names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  if (monthCount >= 12) return "Full calendar year";
  if (monthCount <= 0) return "No complete months";
  return `January through ${names[monthCount - 1]}`;
}

export function monthSpanLabel(startMonth, endMonth) {
  const names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  if (startMonth === 1 && endMonth === 12) return "Full calendar year";
  if (endMonth < startMonth) return "No tracked months";
  if (startMonth === endMonth) return names[startMonth - 1];
  return `${names[startMonth - 1]} through ${names[endMonth - 1]}`;
}
